import request from 'dojo/request';

import Mediator from 'gisw/Classes/Mediator';
import messageObserver from 'gisw/Widgets/MessageManager/MessageObserver';

// @ts-ignore
import auth from './auth';

class MapLayerCredientials {
  constructor () {
    this.oldcredentials = null;
    /** @type {Record<string, NodeJS.Timeout>} */
    this.credentialRenewalTasks = {};
    /** @type {Record<string, any>} */
    this.layerTypeUserId = {};
    // -10 seconds
    this.timerOffset = -10000;
  }

  /**
   * @param {string} type
   * @returns {Promise<string>}
   */
  getMapLayerAuthenticated (type) {
    const curTabId = Mediator.getCurrentTabID();

    if (!Mediator.serverData.FrontEndOption.BoltonMenk && !auth.loggedIn) {
      return Promise.resolve(null);
    } else if (!curTabId) {
      return Promise.reject(Error('Tab ID is not set'));
    }

    return new Promise(resolve => {
      const privateLayerData = { tabID: curTabId, type };

      this.requestDataResults(
        privateLayerData,
        'PrivateLayer/GetPrivateLayerToken'
      ).then(results => {
        if (results && results.Key) {
          const credentialObject = this.createLayerCredientialObject(results);

          this.layerTypeUserId[type] = results.UserID;
          this.crediencialObject = credentialObject;
          this.credentials = credentialObject.credentials;
          this.oldcredentials = dojo.clone(this.credentials);

          const tokentimeout =
            results.Expiration && !isNaN(results.Expiration)
              ? Number.parseFloat(results.Expiration)
              : 60;
          this.renewMapLayerCredentials(type, tokentimeout);
          this.updateAllMapLayersCrediential();

          resolve(results.Key);
        } else {
          if (results.Status && results.Status.Code === 'Error') {
            messageObserver.broadcast({
              title: results.Status.Code,
              message: 'Private Map layer: ' + results.Status.Message
            });
          }

          resolve(null);
        }
      });
    });
  }

  isCredientialsExpired () {
    if (this.credentials && this.credentials.length > 0) {
      for (let i = 0; i < this.credentials.length; i++) {
        if (this.isCredientialExpired(this.credentials[i])) {
          return true;
        }
      }
    }

    return false;
  }

  isCredientialExpired (credential) {
    const now = Date.now();

    if (now > credential.expires + this.timerOffset) {
      return true;
    }

    return false;
  }

  updateAllMapLayersCrediential () {
    if (!Mediator.layermanager || !Mediator.layermanager.mapLayers) {
      return;
    }

    const dynamicLayers = Mediator.layermanager.mapLayers.dynamicLayers;

    if (dynamicLayers) {
      this.updateLayersObjsCrediential(
        dynamicLayers,
        this.layerTypeUserId['submap']
      );
    }
  }

  /**
   * @param {Record<string, import('ol/layer').Image>} layersObjs
   * @param {*} thisLayerUserId
   */
  updateLayersObjsCrediential (layersObjs, thisLayerUserId) {
    if (!layersObjs || !thisLayerUserId) {
      return;
    }

    for (const key in layersObjs) {
      if (layersObjs.hasOwnProperty(key)) {
        const curLayer = layersObjs[key];

        if (
          curLayer &&
          curLayer.get('credential') &&
          this.isCredientialExpired(curLayer.get('credential'))
        ) {
          const credential = this.getCredientialByUserId(thisLayerUserId);
          /** @type {import('ol/source').ImageArcGISRest} */
          // @ts-ignore
          const source = curLayer.getSource();
          const params = source.getParams();

          params.token = credential.token;
          source.updateParams(params);
          curLayer.set('credential', credential);
        }
      }
    }
  }

  getCredientialByUserId (UserId) {
    if (this.credentials) {
      for (let i = 0; i < this.credentials.length; i++) {
        if (this.credentials[i].userId === UserId) {
          return this.credentials[i];
        }
      }
    }

    return null;
  }

  /**
   * @private
   * @param {string} type
   * @param {number} tokentimeout In minutes
   */
  renewMapLayerCredentials (type, tokentimeout) {
    const time = tokentimeout * 60000 + this.timerOffset;

    if (this.credentialRenewalTasks[type]) {
      clearTimeout(this.credentialRenewalTasks[type]);
    }

    if (auth.loggedIn || Mediator.serverData.FrontEndOption.BoltonMenk) {
      this.credentialRenewalTasks[type] = setTimeout(() => {
        if (auth.loggedIn || Mediator.serverData.FrontEndOption.BoltonMenk) {
          this.getMapLayerAuthenticated(type);
        }
      }, time);
    }
  }

  /**
   * @private
   * @param {*} results
   */
  createLayerCredientialObject (results) {
    let userid = '';
    let tokentimeout;

    if (results) {
      this.token = results.Key;
      tokentimeout =
        results.Expiration && isNaN(results.Expiration) === false
          ? parseFloat(results.Expiration)
          : 60;
      userid = results.UserID;
    }

    const now = Date.now();

    const expires = now + tokentimeout * 60000;
    let hostname = '';
    let tokenServiceUrl = '';
    let adminTokenServiceUrl = '';
    let server = '';
    let resource = '';

    if (results.Status && results.Status.Code === 'ServicerName') {
      hostname = 'https://' + results.Status.Message + '.gworks.com';
      tokenServiceUrl = hostname + '/arcgis/tokens/';
      adminTokenServiceUrl = hostname + '/arcgis/admin/generateToken';
      server = hostname + '/arcgis';
      resource = server + '/rest/services';
    }

    if (Mediator.serverData.FrontEndOption.BoltonMenk) {
      hostname = 'https://gis.bolton-menk.com/metia';
      tokenServiceUrl = 'https://gis.bolton-menk.com/metia/tokens/';
      adminTokenServiceUrl =
        'https://gis.bolton-menk.com/metia/admin/generateToken/';
      server = hostname;
      resource = 'https://gis.bolton-menk.com/metia/rest/services';
    }

    const imObject = {
      serverInfos: [
        {
          server: hostname,
          tokenServiceUrl,
          adminTokenServiceUrl,
          shortLivedTokenValidity: tokentimeout,
          currentVersion: 10.22,
          hasServer: true
        }
      ],
      oAuthInfos: [],
      credentials: [
        {
          userId: userid,
          server,
          token: this.token,
          expires,
          validity: 50,
          ssl: false,
          creationTime: now,
          scope: 'server',
          resources: [resource]
        }
      ]
    };
    imObject.credentials = this.getNewCredentialsOrdered(
      this.oldcredentials,
      imObject.credentials[0]
    );
    return imObject;
  }

  getNewCredentialsOrdered (oldcredentials, newcredentials) {
    if (oldcredentials && newcredentials) {
      for (let i = 0; i < oldcredentials.length; i++) {
        if (oldcredentials[i].server === newcredentials.server) {
          oldcredentials[i] = newcredentials;
          return oldcredentials;
        }
      }

      return oldcredentials.concat(newcredentials);
    }

    return [newcredentials];
  }

  /**
   * @param {import('ol/layer').Layer} layerObj
   * @param {boolean} [isJson]
   */
  getLayerURLandToken (layerObj, isJson = false) {
    const source = layerObj.getSource();
    const curCreds = this.credentials && this.credentials[0];

    const resultObj = {
      token: curCreds ? curCreds.token : '',
      tokenString: curCreds ? '?token=' + curCreds.token : '',
      url: source.url_,
      expired: curCreds ? this.isCredientialsExpired() : null
    };

    if (isJson) {
      resultObj.tokenString +=
        resultObj.tokenString.indexOf('?') >= 0 ? '&f=json' : '?f=json';
    }

    return resultObj;
  }

  /**
   * @param {import('ol/layer').Layer} layerObj
   */
  async getAuthenticatedLayerURLandToken (layerObj) {
    if (this.isCredientialsExpired()) {
      await this.getMapLayerAuthenticated('submap');
    }

    return this.getLayerURLandToken(layerObj);
  }

  /**
   * @private
   * @param {*} data
   * @param {string} url
   */
  requestDataResults (data, url) {
    return new Promise((resolve, reject) => {
      request(url, {
        handleAs: 'json',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
          Accept: 'application/json'
        },
        data: JSON.stringify(data),
        method: 'POST'
      }).then(resolve, reject);
    });
  }
}

export default MapLayerCredientials;
